const WEBHOOK_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL;

// Simple queue to avoid hitting Discord's 5 req/2s rate limit
let _queue = [];
let _flushing = false;

async function _flush() {
  if (_flushing || _queue.length === 0) return;
  _flushing = true;
  while (_queue.length > 0) {
    const payload = _queue.shift();
    try {
      await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch {
      // network failure — drop silently so logging never breaks the app
    }
    if (_queue.length > 0) {
      await new Promise((r) => setTimeout(r, 500));
    }
  }
  _flushing = false;
}

function _enqueue(payload) {
  if (!WEBHOOK_URL) return;
  _queue.push(payload);
  _flush();
}

function _truncate(str, max = 1000) {
  if (!str) return "";
  return str.length > max ? str.slice(0, max) + "\n…(truncated)" : str;
}

function _buildEmbed({ title, message, stack, source, color }) {
  const fields = [];

  if (source) {
    fields.push({ name: "📍 Source", value: `\`${_truncate(source, 200)}\``, inline: false });
  }
  if (stack) {
    fields.push({ name: "🔍 Stack trace", value: `\`\`\`\n${_truncate(stack, 800)}\n\`\`\``, inline: false });
  }
  fields.push({ name: "🌐 URL", value: window.location.href, inline: false });
  fields.push({ name: "🕐 Time", value: new Date().toISOString(), inline: true });
  fields.push({
    name: "🖥️ User Agent",
    value: `\`${_truncate(navigator.userAgent, 200)}\``,
    inline: false,
  });

  return {
    embeds: [
      {
        title: `🚨 ${title}`,
        description: `**${_truncate(message, 300)}**`,
        color,
        fields,
        footer: { text: "Indofast Support CRM · Frontend Error" },
      },
    ],
  };
}

// ── Public API ───────────────────────────────────────────

export function logError(error, context = "") {
  const message = error?.message || String(error);
  const stack = error?.stack || "";
  _enqueue(
    _buildEmbed({
      title: "JavaScript Error" + (context ? ` · ${context}` : ""),
      message,
      stack,
      source: context,
      color: 0xe53e3e, // red
    })
  );
}

export function logRenderError(error, componentStack) {
  _enqueue(
    _buildEmbed({
      title: "React Render Error",
      message: error?.message || String(error),
      stack: _truncate(componentStack, 800),
      source: "React Error Boundary",
      color: 0xd69e2e, // amber
    })
  );
}

export function logApiError({ method = "GET", url, status, message, payload }) {
  const isNetworkError = !status;
  const color = isNetworkError ? 0xfc8181 : status >= 500 ? 0xe53e3e : 0xed8936; // red / orange
  const title = isNetworkError
    ? "Network Error (Backend Unreachable)"
    : `API Error ${status}`;

  const fields = [
    { name: "🔗 Endpoint", value: `\`${method.toUpperCase()} ${_truncate(url, 200)}\``, inline: false },
  ];
  if (status) {
    fields.push({ name: "📟 Status", value: `\`${status}\``, inline: true });
  }
  if (payload) {
    fields.push({
      name: "📦 Response payload",
      value: `\`\`\`json\n${_truncate(JSON.stringify(payload, null, 2), 600)}\n\`\`\``,
      inline: false,
    });
  }
  fields.push({ name: "🌐 URL", value: window.location.href, inline: false });
  fields.push({ name: "🕐 Time", value: new Date().toISOString(), inline: true });

  _enqueue({
    embeds: [
      {
        title: `🔴 ${title}`,
        description: `**${_truncate(message, 300)}**`,
        color,
        fields,
        footer: { text: "Indofast Support CRM · API Error" },
      },
    ],
  });
}

export function logUnhandledRejection(reason) {
  const message =
    reason instanceof Error ? reason.message : String(reason ?? "Unhandled promise rejection");
  const stack = reason instanceof Error ? reason.stack : "";
  _enqueue(
    _buildEmbed({
      title: "Unhandled Promise Rejection",
      message,
      stack,
      source: "window.onunhandledrejection",
      color: 0x9f7aea, // purple
    })
  );
}
